/**
 * Cartridge-faithful battle log strings. Centralized here so the
 * pacing-gated `appendLog` call sites in BattleScene stay terse and
 * the wording doesn't drift between event handlers.
 *
 * Names + phrasings match the FRLG cartridge convention. Per the eng
 * review (Issue 7A 2026-05-13), DRY enforcement is the reason this
 * module exists — without it, "It's super effective!" would land
 * inline next to a fail variant that says "It failed!" instead of
 * "But it failed!". Tests assert helper output directly; BattleScene
 * doesn't need to mount for the text contract to be exercised.
 *
 * Tense matches the cartridge: status onset uses past tense for
 * conditions that "happen TO" the target (was poisoned, fell asleep,
 * was burned, was frozen solid); paralysis uses present tense with
 * the cartridge-canonical footnote ("It may not attack!") because
 * paralysis modifies every subsequent turn rather than a single moment.
 */

export type StatusCondition = 'psn' | 'par' | 'brn' | 'slp' | 'frz';
export type CantReason = 'slp' | 'par' | 'frz' | 'flinch';

/** "A critical hit!" — fires on |-crit|. */
export function critText(): string {
  return 'A critical hit!';
}

/** "{source}'s attack missed!" — fires on |-miss|<source>|<target>. */
export function missText(source: string): string {
  return `${source}'s attack missed!`;
}

/** "But it failed!" — fires on |-fail|. Cartridge ships granular
 *  reasons (already-poisoned, weather-mismatch, etc.) but v0.1 uses
 *  the generic phrasing — Phase 2 audit can ladder finer text on
 *  top of the same helper. */
export function failText(): string {
  return 'But it failed!';
}

/** Cartridge-canonical paralysis-onset line — two clauses, present tense.
 *  Used by `statusText` when status === 'par'; exposed top-level because
 *  the two-clause form is unusual relative to the other status onsets. */
export function paralyzedText(target: string): string {
  return `${target} is paralyzed! It may not attack!`;
}

/** First-time status-condition apply text — fires on |-status|<...>. */
export function statusText(target: string, status: StatusCondition): string {
  switch (status) {
    case 'psn':
      return `${target} was poisoned!`;
    case 'par':
      return paralyzedText(target);
    case 'brn':
      return `${target} was burned!`;
    case 'slp':
      return `${target} fell asleep!`;
    case 'frz':
      return `${target} was frozen solid!`;
  }
}

/** "Can't move" text — fires on |cant|<reason>. Sim emits cant the
 *  same beat the turn is forfeited; this text is the in-line reason. */
export function cantText(target: string, reason: CantReason): string {
  switch (reason) {
    case 'slp':
      return `${target} is fast asleep.`;
    case 'par':
      return `${target} is paralyzed and can't move!`;
    case 'frz':
      return `${target} is frozen solid!`;
    case 'flinch':
      return `${target} flinched!`;
  }
}
